import React, { useState } from "react";
import { Box, Typography, Button, Chip, Paper, Stack } from "@mui/material";
import { useDropzone } from "react-dropzone";
import Title from "../../../../components/Title";
import { JsonEditor } from "json-edit-react";
import { useUploadJson } from "../../../../api/suite/use-remote-json";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { useNavigate } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

const UploadJsonForm: React.FC = () => {
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [data, setData] = useState<any>({});
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const uploadMutation = useUploadJson();

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setJsonFile(file);
        try {
          setData(JSON.parse(content));
        } catch {
          showSnackbar("Invalid JSON file", "error");
        }
      };
      reader.readAsText(file);
    },
    accept: { "application/json": [".json"] },
  });

  const handleProcess = () => {
    if (!jsonFile) {
      showSnackbar("No file selected", "error");
      return;
    }
    const updatedJsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([updatedJsonString], { type: "application/json" });
    const updatedFile = new File([blob], jsonFile.name, {
      type: "application/json",
    });
    const formData = new FormData();
    formData.append("file", updatedFile);

    uploadMutation.mutate(formData, {
      onSuccess: () => {
        showSnackbar("Upload successful", "success");
        navigate("/suites");
      },
      onError: (error: any) => {
        const resp = error?.response?.data;
        if (resp?.message && typeof resp.message === "object") {
          const text = JSON.stringify(resp.message, null, 2);
          showSnackbar(text, "error");
        } else {
          const msg = resp?.message || "Upload failed";
          showSnackbar(String(msg), "error");
        }
      },
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Title>Upload JSON</Title>
      <Paper
        {...getRootProps()}
        variant="outlined"
        sx={{
          alignItems: "center",
          bgcolor: "background.level1",
          borderColor: jsonFile ? "primary.main" : "divider",
          borderStyle: "dashed",
          borderWidth: 2,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mb: 2,
          p: { xs: 2.5, sm: 4 },
          textAlign: "center",
          transition: "border-color 160ms ease, background-color 160ms ease",
          "&:hover": {
            bgcolor: "#eef6ff",
            borderColor: "primary.main",
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon color="primary" sx={{ fontSize: 36 }} />
        <Typography sx={{ fontWeight: 800 }}>
          Drop JSON file here or click to select
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 13 }}>
          The manifest can be reviewed before processing.
        </Typography>
      </Paper>
      {jsonFile && (
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 2 }}>
            <InsertDriveFileOutlinedIcon color="action" fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>{jsonFile.name}</Typography>
            <Chip size="small" label="JSON" color="primary" variant="outlined" />
          </Stack>
          <Paper variant="outlined" sx={{ overflow: "hidden", p: 1 }}>
            <JsonEditor data={data} setData={setData} restrictDelete />
          </Paper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleProcess}
            disabled={uploadMutation.isPending}
            sx={{ mt: 2, minWidth: 132 }}
          >
            {uploadMutation.isPending ? "Processing..." : "Process"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default UploadJsonForm;
